import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button, IconButton, TextField, Dialog, DialogActions, DialogContent, DialogContentText,
} from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { get, size } from 'lodash/fp';
import { Formik, Form, FastField } from 'formik';
import * as Yup from 'yup';
import qs from 'query-string';

import { Spinner } from '../../../components';
import { useModal } from '../../../hooks';
import * as tokenActions from '../../token/actions';
import * as actions from '../actions';
import { PhoneNumberField, WrongAnswerDialog } from '../components';
import { closeWebView, isWebView } from '../../../utils';

const Schema = Yup.object().shape({
  phoneNumber: Yup.string()
    .test('length', 'Must be exactly 4 characters', (value) => size(value) === 4)
    .required(),
});

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
  `,
  ActionBar: styled.header`
    display: flex;
    align-items: center;
    min-height: 56px;
    margin-bottom: 1px;
    padding: 0 12px;
    background-color: ${({ theme }) => theme.palette.primary.contrastText};
  `,
  Heading: styled.h2`
    margin: 0 auto 0 2rem;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: 1.25rem;
    font-weight: 500;
    line-height: 1.학;
  `,
  ArrowBack: styled(ArrowBack)`
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  Main: styled.main`
    padding: 48px 16px;
  `,
  Paragraph: styled.p`
    margin: 0 auto 0.75rem;
    max-width: 280px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(14)};
    line-height: 1.43;
    text-align: center;
    word-break: keep-all;
  `,
  Empahsis: styled.em`
    color: ${({ theme }) => theme.palette.secondary.main};
    font-style: normal;
  `,
  TextField: styled(TextField)`
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.palette.grey[500]};
    }
    .MuiInputLabel-root.Mui-focused {
      color: ${({ theme }) => theme.palette.secondary.main};
    }
    .MuiInput-underline::after {
      border-bottom-color: ${({ theme }) => theme.palette.secondary.main};
    }
  `,
  SubmitButton: styled(Button)`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    min-height: 60px;
    border-radius: 0;
  `,
  Button: styled(Button)`
    color:#0c8d6d;
  `,
  RetryButton: styled(Button)`
    color:#e15863;
  `,
};
const Connection = ({ history, location }) => {
  const [redirect, setRedirect] = useState(false);
  const [open, openDialog, closeDialog] = useModal();
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [input, setInput] = useState('');

  const guardianContactNewsletter = useSelector((state) => state.token.guardianContactNewsletter, shallowEqual);
  const linked = useSelector((state) => state.connection.linked, shallowEqual);

  const dispatch = useDispatch();

  const handleSubmit = useCallback((values) => {
    setInput(values);
    const phoneNumber = get('guardian_contact.mobile', guardianContactNewsletter.data);
    const isCorrect = phoneNumber && (phoneNumber.substring(size(phoneNumber) - 4) === values.phoneNumber);

    return isCorrect
      ? dispatch(actions.linkGuardianContactToAccountRequest())
      : openDialog();
  }, [dispatch, guardianContactNewsletter.data, openDialog]);

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleRetryConnection = () => {
    handleCloseErrorDialog();
    handleSubmit(input);
  };

  useEffect(() => {
    const { token } = qs.parse(location.search);

    dispatch(tokenActions.parseNewsletterTokenRequest({ token }));
  }, [dispatch, location.search]);

  useEffect(() => {
    if (linked.data && get('from', location.state)) {
      setRedirect(true);

      return () => dispatch(actions.initLinked());
    }

    return () => {};
  }, [dispatch, linked.data, location.state]);

  useEffect(() => {
    if (linked.error) {
      setOpenErrorDialog(true);

      dispatch(actions.initLinked());
    }
  }, [dispatch, linked.error]);

  const handleBack = () => {
    if (isWebView()) {
      closeWebView();
    } else {
      history.goBack();
    }
  };

  return (
    <>
      <S.Wrapper>
        <S.ActionBar>
          <IconButton type="button" size="small" onClick={handleBack}>
            <S.ArrowBack />
          </IconButton>
          <S.Heading>보호자 정보 연동</S.Heading>
        </S.ActionBar>
        <S.Main>
          <S.Paragraph>
            정보 연동을 위하여
            {' '}
            <S.Empahsis>본인 휴대폰 뒷번호 4자리</S.Empahsis>
            를 입력해주세요. 정보를 연동하면 클래스팅 앱으로 가정통신문을 편하게 이용하실 수 있습니다.
          </S.Paragraph>
          <Formik
            initialValues={{ phoneNumber: '' }}
            validationSchema={Schema}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form>
                <FastField name="phoneNumber" component={PhoneNumberField} />
                <S.SubmitButton type="submit" color="secondary" variant="contained">
                  아이디와 휴대폰 번호 연동하기
                </S.SubmitButton>
              </Form>
            )}
          </Formik>
        </S.Main>
      </S.Wrapper>
      <WrongAnswerDialog open={open} onClose={closeDialog} />
      {linked.loading && <Spinner />}
      {redirect && (
        <Redirect
          to={{
            pathname: location.state.from,
            search: location.search,
            state: { from: location.pathname },
          }}
        />
      )}
      <Dialog
        open={openErrorDialog}
        onClose={handleCloseErrorDialog}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            계정 정보 연동에 실패했어요.
            <br />
            보안을 위해 휴대폰번호는 하나의 계정에만 연동되니, 여러 계정을 사용하시는 경우 로그인 정보를 다시 확인해주세요.
            <br />
            문제가 계속 발생할 경우 고객센터로 문의해주세요.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <S.Button
            onClick={handleCloseErrorDialog}
          >
            닫기
          </S.Button>
          <S.RetryButton
            onClick={handleRetryConnection}
          >
            재시도
          </S.RetryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

Connection.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    state: PropTypes.shape({
      from: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export default Connection;
